import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { PlanProvider } from './contexts/PlanContext';
import { WalletProvider } from './contexts/WalletContext';
import GlobalLoadingBar from './components/GlobalLoadingBar';
import RouteAnalytics from './components/RouteAnalytics';
import { ChatWorkspace } from './components/chatHome';
import AdminCostPage from './pages/AdminCostPage';
import EmailEditorPage from './pages/EmailEditorPage';
import Landing from './pages/Landing';
import { GenerativeChatSpike, GalleryPage } from './spikes/generativeUI';
import { AgentLinkSpike } from './spikes/agentLink';
import BoardSpike from './spikes/boardSpike/BoardSpike';
import StudioSpike from './spikes/boardSpike/StudioSpike';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import FAQ from './pages/FAQ';
import Pricing from './pages/Pricing';
import AccountSettings from './pages/AccountSettings';
import WalletPage from './pages/WalletPage';
import WalletHistoryPage from './pages/WalletHistoryPage';
import PublicAgentPage from './pages/PublicAgentPage';
import PublicAgentsPage from './pages/PublicAgentsPage';
import TutorialsIndex from './pages/TutorialsIndex';
import TutorialPage from './pages/TutorialPage';
import AuthorPage from './pages/AuthorPage';
import Storyboard from './pages/Storyboard';
import OrganizationHome from './pages/OrganizationHome';
import ConversationInvitePage from './pages/ConversationInvitePage';
import MediaViewerPage from './pages/MediaViewerPage';
import JoinMediaPage from './pages/JoinMediaPage';
import UnsubscribePage from './pages/UnsubscribePage';
import UpgradePrompt from './components/UpgradePrompt';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import WorkstationChrome from './components/chatHome/WorkstationChrome';
import { WorkstationProvider } from './contexts/WorkstationContext';
import { rememberReturnPath } from './utils/returnPath';
import NotFound from './pages/NotFound';

/* The STORE pages are route-split: the public one is reached by strangers following a shared link, so it must
   not carry the signed-in app's bundle with it. */
const StorePage = React.lazy(() => import('./pages/StorePage'));
const PublicStorePage = React.lazy(() => import('./pages/PublicStorePage'));

// Loading Component
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-secondary-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-secondary-900 dark:border-white mx-auto"></div>
      <p className="mt-4 text-secondary-600 dark:text-secondary-400 font-medium">Loading...</p>
    </div>
  </div>
);



// The quiet in-chrome placeholder while a route-split page loads (the rail stays; only the page area waits).
const PageLoading: React.FC = () => (
  <div className="flex-1 flex items-center justify-center text-[13px] text-secondary-500 dark:text-secondary-400">
    Loading...
  </div>
);

// Legacy /guides/:slug → /tutorials/:slug (guides consolidated into tutorials; slugs unchanged).
const GuideRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={slug ? `/tutorials/${slug}` : '/tutorials'} replace />;
};

// Protected Route Component
/**
 * Every signed-in page goes through here, which is exactly why the workstation's activity bar is mounted
 * here too (owner 2026-08-23: the navigation column *"needs to always be there"*). Chrome that belongs to
 * the whole signed-in app cannot live inside one screen's component.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;
  if (!user) {
    // A deep link opened while signed out comes back here after sign-in instead of being forgotten.
    rememberReturnPath(location.pathname + location.search);
    return <Navigate to="/" replace />;
  }

  /* The boundary sits INSIDE the chrome, so a route-split page that is still loading keeps the activity bar on
     screen. Without it, clicking into a lazy page suspends with nothing to catch it and the error boundary
     replaces the whole app. */
  return (
    <WorkstationChrome>
      <React.Suspense fallback={<PageLoading />}>{children}</React.Suspense>
    </WorkstationChrome>
  );
};

// Main App Component
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <WorkstationProvider>
        <WorkspaceProvider>
          <PlanProvider>
          <WalletProvider>
          <NotificationProvider>
            <LoadingProvider>
              <Router future={{ v7_relativeSplatPath: true }}>
                <RouteAnalytics />
                <GlobalLoadingBar />
                <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/spike/generative-ui" element={<GenerativeChatSpike />} />
                <Route path="/spike/gallery" element={<GalleryPage />} />
                <Route path="/spike/board" element={<BoardSpike />} />
                <Route path="/spike/studio" element={<StudioSpike />} />
                <Route path="/spike/agent-link" element={<AgentLinkSpike />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/pricing" element={<Pricing />} />
                {/* Public tutorials + AI-Writer authors (crawlable, SEO/AEO/GEO) — no auth. */}
                <Route path="/tutorials" element={<TutorialsIndex />} />
                <Route path="/tutorials/:slug" element={<TutorialPage />} />
                <Route path="/authors/:slug" element={<AuthorPage />} />
                {/* Legacy /guides consolidated into /tutorials — redirect (same slugs) so old links survive. */}
                <Route path="/guides" element={<Navigate to="/tutorials" replace />} />
                <Route path="/guides/:slug" element={<GuideRedirect />} />
                {/* Internal design storyboard — components + page previews (noindex, robots-blocked). */}
                <Route path="/storyboard" element={<Storyboard />} />
                {/* Public agent directory (crawlable, SEO/GEO) — no auth. */}
                <Route path="/agents" element={<PublicAgentsPage />} />
                <Route path="/agent/:slug" element={<PublicAgentPage />} />
                <Route path="/agent/:slug/details" element={<PublicAgentPage />} />
                <Route path="/join-chat/:token" element={<ConversationInvitePage />} />
                <Route path="/m/:mediaId" element={<MediaViewerPage />} />
                {/* A CREATOR'S STORE, at the short address they share. Public on purpose: outside
                    ProtectedRoute, because a storefront that needs a login is not a storefront. */}
                <Route
                  path="/s/:storeId"
                  element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <PublicStorePage />
                    </React.Suspense>
                  }
                />
                <Route path="/join-media/:mediaId" element={<JoinMediaPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/org/:slug" element={<OrganizationHome />} />
                {/* /dashboard and /group/:slug share ONE persistent ChatWorkspace (a layout
                    route) so navigating between them — e.g. after creating a chat — does NOT
                    remount it. A remount would re-run useChatSessions and blank the message
                    stream until it refetched. ChatWorkspace reads :slug via useParams.
                    100dvh (not h-screen/100vh) so the layout matches the VISIBLE viewport on
                    mobile — otherwise the dynamic browser address bar pushes the list footer
                    (account/theme/logout) below the fold. */}
                <Route
                  element={
                    <ProtectedRoute>
                      {/* w-full, NOT w-screen: the workstation now sits in the column BESIDE the activity
                          bar, so 100vw made it 48px too wide and pushed its right edge — the crew pane's
                          rail and toggle — off the screen entirely (owner 2026-08-23: "can't see it now"). */}
                      <div className="h-full w-full overflow-hidden">
                        <ChatWorkspace />
                      </div>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={null} />
                  {/* An open production is addressable: /dashboard/{slug}. Same layout route, so opening one
                      does not remount the workspace. ChatThread resolves the handle (slug OR id). */}
                  <Route path="/dashboard/:production" element={null} />
                  {/* Each stage is a dedicated page: /dashboard/{slug}/story, /story/cast, /shots, /publish.
                      Same layout route, so moving through the journey never remounts the workspace. */}
                  <Route path="/dashboard/:production/:stage" element={null} />
                  <Route path="/group/:slug" element={null} />
                </Route>
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminCostPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/email-editor"
                  element={
                    <ProtectedRoute>
                      <EmailEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account-settings"
                  element={
                    <ProtectedRoute>
                      <AccountSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/store"
                  element={
                    <ProtectedRoute>
                      <StorePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wallet"
                  element={
                    <ProtectedRoute>
                      <WalletPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wallet/history"
                  element={
                    <ProtectedRoute>
                      <WalletHistoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <UpgradePrompt />
              <UpdateAvailableBanner />
            </Router>
          </LoadingProvider>
        </NotificationProvider>
          </WalletProvider>
          </PlanProvider>
        </WorkspaceProvider>
        </WorkstationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;